import { Button, Flex, Space, Switch } from "antd";
import { isArray } from "lodash-es";
import { FormattedMessage, useIntl } from "react-intl";
import PermissionCheck from "~Common/PermissionCheck";
import {
  DataTableColumnChooser
} from "~components/DataTable/components/DataTableColumnChooser/DataTableColumnChooser.tsx";
import { FilterPanel } from "~components/DataTable/components/FilterPanel/FilterPanel.tsx";
import { SecondaryFilterLabel } from "~components/DataTable/components/FilterPanel/SecondaryFilterLabel.tsx";
import SearchFilter from "~components/SearchFilter/SearchFilter.tsx";
import { type PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { useTableFilters } from "~components/DataTable/hooks/useTableFilters.ts";
import { FiltersConfig } from "~components/DataTable/types/filtersConfig.ts";
import { DataTableConfig } from "~types/dataTableConfig.ts";
import { SearchConfig } from "~components/DataTable/types/searchConfig.ts";

type DataTableHeaderProps<T = any> = {
  filtersConfig?: FiltersConfig;
  searchConfig?: SearchConfig;
  handleAdd?: () => void;
  permissionNames: Array<PermissionNames>;
  addButtonIntlKey: string;
  tableColumns: DataTableColumn<T>[];
  setSelectedColumns: (columns: Array<string>) => void;
  columnChooserLocalStorageKey: string;
  dataTableUiConfiguration: DataTableConfig;
  searchPlaceholderIntlKey?: string;
  onMassEditModeChangedHandler?: (checked: boolean) => void;
  handleRefresh?: () => void;
};

export const DataTableHeader = <T = any>({
                                           filtersConfig,
  searchConfig,
                                           searchPlaceholderIntlKey,
                                           permissionNames,
                                           onMassEditModeChangedHandler,
                                           handleRefresh,
                                           tableColumns,
                                           setSelectedColumns,
                                           columnChooserLocalStorageKey,
                                           dataTableUiConfiguration,
                                         }: DataTableHeaderProps<T>) => {
  const { formatMessage } = useIntl();
  const { filters, currentFilter, handleFilterChange } = filtersConfig ?? {
    filters: [],
  };

  const { primaryFilters, secondaryFilters } = useTableFilters(filters, dataTableUiConfiguration.filtersConfig || [])
  return (
    <div className="data-table-header">
      <div className="data-table-header-first-row">
        <div className="data-table-filters">
          {primaryFilters.map(({ filterKey, filterElement }) => (
            <div key={filterKey}>
              {filterElement({
                value: currentFilter?.[filterKey],
                onChange: (values: string[]) => handleFilterChange?.(filterKey, values),
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="data-table-header-second-row">
        {filters && (
          <Flex gap={10} justify="space-between" align="center" wrap="wrap">
            {handleFilterChange && secondaryFilters && secondaryFilters.length > 0 && (
              <FilterPanel filters={secondaryFilters} onChangeFilters={handleFilterChange}
                           currentFilter={currentFilter || {}}/>
            )}
            {secondaryFilters.map((filter) => {
              const { filterKey, secondaryFilterLabel } = filter;
              const filterValue = currentFilter?.[filterKey];
              return (
                filterValue &&
                !(isArray(filterValue) && filterValue.length === 0) && (
                  <Flex key={filterKey} className="filter-panel-label">
                    {secondaryFilterLabel ? secondaryFilterLabel : <SecondaryFilterLabel value={filterValue}/>}
                    {handleFilterChange && (
                      <Button
                        type="text"
                        size={"small"}
                        icon={<i className="fas fa-x" style={{ fontSize: "8px" }}/>}
                        className="data-table-filter-panel-clear-button"
                        onClick={() => {
                          handleFilterChange(filterKey, undefined);
                        }}
                      />
                    )}
                  </Flex>
                )
              );
            })}
          </Flex>
        )}
        <div className="data-table-controls">
          <div className="action-buttons-wrapper">
            <div className="search-wrapper">
              {searchConfig && (
                <SearchFilter searchValue={searchConfig.searchText} doSearch={searchConfig?.handleSearch}
                              placeholderIntlKey={searchPlaceholderIntlKey || "search.two.letters"}/>
              )}
            </div>
            {onMassEditModeChangedHandler && (
              <PermissionCheck permissionKeys={permissionNames} permissionOptions={[PermissionOperations.UPDATE]}>
                <Space>
                  {formatMessage({ id: "edit.mode" })}
                  <Switch onChange={onMassEditModeChangedHandler}/>
                </Space>
              </PermissionCheck>
            )}
            {handleRefresh && (
              <Button onClick={handleRefresh} type="text" className="table-header-button">
                <i className="fa-solid fa-arrows-rotate"></i>
                <FormattedMessage id="data.table.button.refresh"/>
              </Button>
            )}
            <DataTableColumnChooser
              setSelectedColumns={setSelectedColumns}
              tableConfiguration={dataTableUiConfiguration.columnsConfig}
              localStorageKey={columnChooserLocalStorageKey}
              columns={tableColumns.map((x) => ({ key: x.key || x.dataIndex, title: x.title }) as {
                key: string;
                title: string
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
